import { Resume, SingleTemplateConfigSettings, SingleTemplateSettings } from '@rio/types'
import { useI18n } from 'builder/hooks/useI18n'
import { SelectValue } from 'builder/components/Select/types'
import { SettingsFieldChangeHandler } from 'builder/modules/resumeEditor'
import { useUpdateTemplateSettings } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/hooks/useUpdateTemplateSettings'

import { FontWeightFieldIds } from '../types'
import { FALLBACK_FONT_WEIGHTS, TEXT_TRANSLATION } from '../constants'

type Props = {
  resume: Resume
  currentTemplate: SingleTemplateConfigSettings | null
  resumeSpecificTemplateSettings: SingleTemplateSettings
}

export const useGetFontWeights = (props: Props) => {
  const { i18n } = useI18n()
  const { currentTemplate, resumeSpecificTemplateSettings } = props
  const fontWeights = currentTemplate?.settings?.fontWeights || []
  const { updateTemplateSettings } = useUpdateTemplateSettings(props.resume)

  const handleTemplateSettingsChange: SettingsFieldChangeHandler = (settingsName, newValue) => {
    updateTemplateSettings({ settingsName, value: newValue })
  }

  const getFontWeightField = (field: FontWeightFieldIds) => {
    const defaultValue = currentTemplate?.settings?.[field]
    const currentValue = resumeSpecificTemplateSettings?.[field]
    const fallbackValue = FALLBACK_FONT_WEIGHTS[field]

    return currentValue || defaultValue || fallbackValue
  }

  const onSelect = (field: FontWeightFieldIds, newValue: SelectValue) => {
    if (typeof newValue !== 'string') return
    const currentValue = getFontWeightField(field)
    handleTemplateSettingsChange(field, newValue, currentValue)
  }

  const primaryFWValue = getFontWeightField('primaryFontWeight')
  const secondaryFWValue = getFontWeightField('secondaryFontWeight')
  const bodyFWValue = getFontWeightField('bodyFontWeight')
  const sectionTitlesFWValue = getFontWeightField('sectionTitlesFontWeight')

  const getLabel = (field: FontWeightFieldIds) => {
    switch (field) {
      case 'primaryFontWeight':
        return i18n.t(`${TEXT_TRANSLATION}.primary_heading_label`)
      case 'secondaryFontWeight':
        return i18n.t(`${TEXT_TRANSLATION}.secondary_heading_label`)
      case 'bodyFontWeight':
        return i18n.t(`${TEXT_TRANSLATION}.body_label`)
      case 'sectionTitlesFontWeight':
        return i18n.t(`${TEXT_TRANSLATION}.section_titles_label`)
    }
  }

  return {
    options: fontWeights,
    bodySelected: bodyFWValue,
    primarySelected: primaryFWValue,
    secondarySelected: secondaryFWValue,
    sectionTitlesSelected: sectionTitlesFWValue,
    onSelectFontWeight: onSelect,
    getFontWeightLabel: getLabel,
  }
}
